import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { addOnProducts, oneOffProducts, type PickablePlan, type PickableProduct } from './storefrontProducts'
import { useMoney } from '@/lib/hooks/useMoney'
import type { StorefrontMode } from '@/types/storefront'

const MODES: StorefrontMode[] = ['all', 'selected']

interface StorefrontProductPickerProps {
  mode: StorefrontMode
  onModeChange: (mode: StorefrontMode) => void
  /** Picked product ids (one-offs and add-ons); the position is the display order. */
  picked: string[]
  onPickedChange: (picked: string[]) => void
  /** Picked plan ids; a plan is picked whole, never one of its billing options. */
  pickedPlans?: string[]
  onPickedPlansChange?: (picked: string[]) => void
  products: PickableProduct[]
  plans?: PickablePlan[]
  /** Legacy picks that vanished: billing options of no plan (criterion 13). */
  droppedOptionCount?: number
  /** Shown in `selected` mode when `products` is empty. */
  emptyMessage?: string
  /** Replaces the checklist in `selected` mode, e.g. while the products load. */
  listOverride?: ReactNode
  disabled?: boolean
  className?: string
}

/**
 * The storefront mode pills and the picker (F-29 phase 3), in three groups:
 * one-off products, plans (the whole plan, its billing options come along and
 * are never checked alone) and add-ons. Stateless: whoever renders it owns
 * the selection, so the store settings dialog and the space creation wizard
 * share one picker. The order items are checked in is the order the store
 * shows them.
 */
export function StorefrontProductPicker({
  mode,
  onModeChange,
  picked,
  onPickedChange,
  pickedPlans = [],
  onPickedPlansChange,
  products,
  plans = [],
  droppedOptionCount = 0,
  emptyMessage,
  listOverride,
  disabled = false,
  className
}: StorefrontProductPickerProps) {
  const { t } = useTranslation(['common', 'monetization'])
  const formatMoney = useMoney()

  const toggle = (list: string[], onChange: (next: string[]) => void, id: string) =>
    onChange(list.includes(id) ? list.filter((value) => value !== id) : [...list, id])

  const statusLabel = (status: string) =>
    status === 'ACTIVE'
      ? t('monetization:products.statusActive')
      : status === 'DRAFT'
        ? t('monetization:products.statusDraft')
        : t('monetization:products.statusInactive')

  const optionDetails = (product: PickableProduct) => {
    const price = formatMoney(Number(product.price) || 0, product.currency)
    if (product.billingInterval) {
      const interval =
        product.billingInterval === 'YEARLY'
          ? t('monetization:products.perYear')
          : product.billingInterval === 'MONTHLY'
            ? t('monetization:products.perMonth')
            : product.billingInterval
      const trial = product.trialDays ? ` • ${t('monetization:products.trialDays', { count: product.trialDays })}` : ''
      return `${price}${interval}${trial}`
    }
    return price
  }

  const row = (
    id: string,
    list: string[],
    onChange: (next: string[]) => void,
    title: string,
    details: string,
    inactive: boolean,
    badge?: ReactNode
  ) => {
    const position = list.indexOf(id)
    const checked = position !== -1
    return (
      <li key={id}>
        <label
          className={cn(
            'flex items-center gap-3 px-3 py-2.5',
            disabled ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50'
          )}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggle(list, onChange, id)}
            disabled={disabled}
            className="h-4 w-4 flex-shrink-0"
          />
          <span className="min-w-0 flex-1">
            <span className="flex flex-wrap items-center gap-x-2">
              <span className="block truncate text-sm font-medium text-gray-900">{title}</span>
              {badge}
            </span>
            <span className="block text-xs text-gray-500">
              {details}
              {inactive && (
                <> · <span className="text-amber-700">{statusLabel('INACTIVE')} — {t('common:storefront.notShownWhileInactive')}</span></>
              )}
            </span>
          </span>
          {checked && (
            <span className="flex-shrink-0 rounded-md bg-primary-50 px-2 py-0.5 text-xs font-medium text-primary-700">
              {position + 1}
            </span>
          )}
        </label>
      </li>
    )
  }

  const oneOffs = oneOffProducts(products)
  const addOns = addOnProducts(products)
  const totalPicked = picked.length + pickedPlans.length

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('common:storefront.modeLabel')}>
        {MODES.map((value) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            onClick={() => onModeChange(value)}
            disabled={disabled}
            data-testid={`storefront-mode-${value}`}
            className={cn(
              'rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60',
              mode === value ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {t(`common:storefront.mode.${value}`)}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500">{t(`common:storefront.modeHint.${mode}`)}</p>

      {mode === 'selected' && droppedOptionCount > 0 && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          {t('common:storefront.droppedOptionsWarning', { count: droppedOptionCount })}
        </p>
      )}

      {mode === 'selected' &&
        (listOverride ? (
          listOverride
        ) : products.length === 0 && plans.length === 0 ? (
          <p className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
            {emptyMessage ?? t('common:storefront.noProducts')}
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">{t('common:storefront.pickedCount', { count: totalPicked })}</p>

            {oneOffs.length > 0 && (
              <section>
                <h4 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('common:storefront.groups.products')}
                </h4>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {oneOffs.map((product) =>
                    row(product.id, picked, onPickedChange, product.title, optionDetails(product), product.status !== 'ACTIVE')
                  )}
                </ul>
              </section>
            )}

            {plans.length > 0 && (
              <section>
                <h4 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('common:storefront.groups.plans')}
                </h4>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {plans.map((plan) =>
                    row(
                      plan.id,
                      pickedPlans,
                      onPickedPlansChange ?? (() => {}),
                      plan.name,
                      plan.options.map(optionDetails).join(' · '),
                      false,
                      plan.visibility === 'link_only' ? (
                        <span className="rounded bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-700">
                          {t('common:storefront.linkOnlyMarker')}
                        </span>
                      ) : undefined
                    )
                  )}
                </ul>
                <p className="mt-1 text-xs text-gray-500">{t('common:storefront.planPickHint')}</p>
              </section>
            )}

            {addOns.length > 0 && (
              <section>
                <h4 className="mb-1 mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  {t('common:storefront.groups.addOns')}
                </h4>
                <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                  {addOns.map((product) =>
                    row(product.id, picked, onPickedChange, product.title, optionDetails(product), product.status !== 'ACTIVE')
                  )}
                </ul>
              </section>
            )}
            <p className="text-xs text-gray-500">{t('common:storefront.orderHint')}</p>
          </div>
        ))}
    </div>
  )
}
